/*
Origin Core 1.0

called from /snippets/scripts.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines a custom web component <variant-picker> that manages variant selection for Shopify products. 
It listens for change events, updates the selected option, dispatches custom events, and fetches updated product or variant data via AJAX. 
The component morphs the DOM to reflect changes, updates the main content or just the variant picker as needed, and synchronizes the browser URL with the selected variant. 
It supports combined listings and ensures only the latest fetch request is processed. 
The component is registered if not already defined.

*/

import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { swatchUrlExists } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Promise<HTMLElement | null> | null} */
  #ringSizeGuideSourceRequest = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);
    this.#syncSecondOptionSwatchVisibility();
    this.#syncRingSizeGuideAvailability();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.#syncRingSizeGuideAvailability, { once: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);
  }

  #getRingSizeDialog() {
    return this.querySelector('#RingSizeDialog-' + this.dataset.sectionId);
  }

  #getSecondOptionDialog() {
    return this.querySelector('#SecondProductOptionDialog-' + this.dataset.sectionId);
  }

  #getRingSizeGuideDialog() {
    const dialogId = this.dataset.ringSizeGuideDialogId || 'RingSizeGuideDialog-' + this.dataset.sectionId;
    return this.querySelector(`#${dialogId}`);
  }

  #getRingSizeGuideContent() {
    return this.querySelector('[data-ring-size-guide-content]');
  }

  #getRingSizeGuideSource() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    return document.querySelector(`[data-ring-size-guide-source][data-product-id="${productId}"]`);
  }

  async #ensureRingSizeGuideSource() {
    const existingSource = this.#getRingSizeGuideSource();
    if (existingSource instanceof HTMLElement) return existingSource;

    const sourceUrl = this.dataset.ringSizeGuideSourceUrl;
    if (!sourceUrl) return null;

    if (!this.#ringSizeGuideSourceRequest) {
      this.#ringSizeGuideSourceRequest = fetch(sourceUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Size guide request failed with status ${response.status}`);
          }

          return response.text();
        })
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const productId = this.dataset.productId;
          const source = productId
            ? html.querySelector(`[data-ring-size-guide-source][data-product-id="${productId}"]`)
            : null;

          if (!(source instanceof HTMLElement)) return null;

          const sourceClone = /** @type {HTMLElement} */ (source.cloneNode(true));
          this.append(sourceClone);
          return sourceClone;
        })
        .catch((error) => {
          console.error(error);
          return null;
        })
        .finally(() => {
          this.#ringSizeGuideSourceRequest = null;
        });
    }

    return this.#ringSizeGuideSourceRequest;
  }

  async #closeDialogComponent(dialogComponent) {
    if (!dialogComponent) return;

    if (typeof dialogComponent.closeDialog === 'function') {
      await dialogComponent.closeDialog();
      return;
    }

    const dialogElement = dialogComponent.querySelector('dialog');
    if (dialogElement instanceof HTMLDialogElement && dialogElement.open) {
      dialogElement.classList.remove('dialog-open', 'dialog-closing');
      dialogElement.close();
    }
  }

  async #closeRingSizeDialog() {
    await this.#closeDialogComponent(this.#getRingSizeDialog());
  }

  async #closeSecondOptionDialog() {
    await this.#closeDialogComponent(this.#getSecondOptionDialog());
  }

  #forceCloseDialogComponent(dialogComponent) {
    const dialogElement = dialogComponent?.querySelector('dialog');
    if (dialogElement instanceof HTMLDialogElement && dialogElement.open) {
      dialogElement.classList.remove('dialog-open', 'dialog-closing');
      dialogElement.close();
    }
  }

  #forceCloseRingSizeDialog() {
    this.#forceCloseDialogComponent(this.#getRingSizeDialog());
  }

  #forceCloseSecondOptionDialog() {
    this.#forceCloseDialogComponent(this.#getSecondOptionDialog());
  }

  async #showRingSizeGuideDialog() {
    const source = await this.#ensureRingSizeGuideSource();
    this.#syncRingSizeGuideAvailability();
    if (!(source instanceof HTMLElement)) {
      const fallbackUrl = this.querySelector('[data-ring-size-size-guide-trigger]')?.dataset.sizeGuideUrl;
      if (fallbackUrl) window.location.assign(fallbackUrl);
      return;
    }

    const sizeGuideDialog = this.#getRingSizeGuideDialog();
    if (!sizeGuideDialog) return;

    await this.#closeRingSizeDialog();

    if (typeof sizeGuideDialog.showDialog === 'function') {
      sizeGuideDialog.showDialog();
      return;
    }

    const dialogElement = sizeGuideDialog.querySelector('dialog');
    if (dialogElement instanceof HTMLDialogElement && !dialogElement.open) {
      dialogElement.showModal();
      setTimeout(() => {
        dialogElement.classList.add('dialog-open');
      });
    }
  }

  #syncRingSizeGuideAvailability = () => {
    const source = this.#getRingSizeGuideSource();
    const hasSource = source instanceof HTMLElement;
    const canLoadSource = Boolean(this.dataset.ringSizeGuideSourceUrl);
    const contentRoot = this.#getRingSizeGuideContent();
    const title = this.querySelector('[data-ring-size-guide-title]');

    this.querySelectorAll('[data-ring-size-size-guide-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;

      trigger.classList.toggle('hidden', !hasSource && !canLoadSource);

      if (hasSource && source.dataset.ringSizeGuideTriggerLabel) {
        trigger.textContent = source.dataset.ringSizeGuideTriggerLabel;
      }
    });

    if (title instanceof HTMLElement && hasSource && source.dataset.ringSizeGuideDrawerTitle) {
      title.textContent = source.dataset.ringSizeGuideDrawerTitle;
    }

    if (!(contentRoot instanceof HTMLElement)) return;

    const introRoot = contentRoot.querySelector('[data-ring-size-guide-content-intro]');
    const tableRoot = contentRoot.querySelector('[data-ring-size-guide-content-table]');
    const hasSlottedContent = introRoot instanceof HTMLElement && tableRoot instanceof HTMLElement;

    if (!hasSource) {
      if (hasSlottedContent) {
        introRoot.innerHTML = '';
        tableRoot.innerHTML = '';
      } else {
        contentRoot.innerHTML = '';
      }

      contentRoot.classList.add('hidden');
      return;
    }

    const sourceTitle = source.querySelector('[data-ring-size-guide-source-title]');
    const sourceRichText = source.querySelector('[data-ring-size-guide-source-richtext]');
    const sourceTable = source.querySelector('[data-ring-size-guide-source-table]');
    const linkText = source.dataset.ringSizeGuideLinkText || '';
    const linkUrl = source.dataset.ringSizeGuideLinkUrl || '';

    const introHtml = `
      ${sourceTitle instanceof HTMLElement ? sourceTitle.innerHTML : ''}
      ${sourceRichText instanceof HTMLElement ? sourceRichText.innerHTML : ''}
      ${linkText && linkUrl ? `<a href="${linkUrl}" class="ring-size-guide-content__link alt-body-tiny anim-link-reverse pb-2">${linkText}</a>` : ''}
    `;

    if (hasSlottedContent) {
      introRoot.innerHTML = introHtml;
      tableRoot.innerHTML = sourceTable instanceof HTMLElement ? sourceTable.innerHTML : '';
    } else {
      contentRoot.innerHTML = `
        ${introHtml}
        ${sourceTable instanceof HTMLElement ? sourceTable.innerHTML : ''}
      `;
    }

    contentRoot.classList.remove('hidden');
  };

  #handleClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const sizeGuideTrigger = event.target.closest('[data-ring-size-size-guide-trigger]');
    if (sizeGuideTrigger instanceof HTMLButtonElement) {
      event.preventDefault();
      await this.#showRingSizeGuideDialog();
      return;
    }

    const slideoutSubmitButton = event.target.closest('[data-ring-size-submit]');
    if (slideoutSubmitButton instanceof HTMLButtonElement) {
      event.preventDefault();

      const productContainer = this.closest('.shopify-section, dialog, product-card');
      const addToCartButton = productContainer?.querySelector('product-form-component [ref="addToCartButton"]');

      await this.#closeRingSizeDialog();

      if (addToCartButton instanceof HTMLButtonElement) {
        addToCartButton.click();
      }

      return;
    }

    const secondOptionSubmitButton = event.target.closest('[data-second-option-submit]');
    if (!(secondOptionSubmitButton instanceof HTMLButtonElement)) return;

    event.preventDefault();

    const productContainer = this.closest('.shopify-section, dialog, product-card');
    const addToCartButton = productContainer?.querySelector('product-form-component [ref="addToCartButton"]');

    await this.#closeSecondOptionDialog();

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.click();
    }
  };

  #handleCartAdd = (event) => {
    if (event?.detail?.data?.didError) return;
    if (event?.detail?.data?.productId && event.detail.data.productId !== this.dataset.productId) return;

    this.#forceCloseRingSizeDialog();
    this.#forceCloseSecondOptionDialog();
  };

  async #bindSwatchFrameVisibility(frame, image) {
    if (!(frame instanceof HTMLElement) || !(image instanceof HTMLImageElement)) return;
    const src = image.currentSrc || image.getAttribute('src') || '';
    frame.classList.add('hidden');
    frame.classList.remove('inline-flex');

    const exists = await swatchUrlExists(src);
    frame.classList.toggle('hidden', !exists);
    frame.classList.toggle('inline-flex', exists);
  }

  #syncSecondOptionSwatchVisibility() {
    const productContainer = this.closest('.shopify-section, dialog, product-card');
    if (!productContainer) return;

    productContainer.querySelectorAll('[data-second-option-swatch-frame]').forEach((frame) => {
      if (!(frame instanceof HTMLElement)) return;
      const image = frame.querySelector('[data-second-option-swatch-image]');
      void this.#bindSwatchFrameVisibility(frame, image);
    });

    productContainer.querySelectorAll('[data-second-option-trigger-swatch-frame]').forEach((frame) => {
      if (!(frame instanceof HTMLElement)) return;
      const image = frame.querySelector('[data-second-option-trigger-swatch]');
      void this.#bindSwatchFrameVisibility(frame, image);
    });
  }

  #syncRingSizeUi(target) {
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.dataset.ringSizeValue) return;

    const productContainer = this.closest('.shopify-section, dialog, product-card');
    productContainer?.querySelectorAll('[data-ring-size-trigger-value]').forEach((triggerValue) => {
      if (triggerValue instanceof HTMLElement) {
        triggerValue.textContent = target.dataset.ringSizeValue || '';
      }
    });

    productContainer?.querySelectorAll('[data-ring-size-status-text]').forEach((statusText) => {
      if (statusText instanceof HTMLElement) {
        statusText.textContent = target.dataset.ringSizeStatusText || '';
      }
    });

    productContainer?.querySelectorAll('[data-ring-size-status-detail]').forEach((statusDetail) => {
      if (statusDetail instanceof HTMLElement) {
        statusDetail.textContent = target.dataset.ringSizeStatusDetail || '';
      }
    });

    productContainer?.querySelectorAll('[data-ring-size-status-detail-card]').forEach((statusDetailCard) => {
      if (statusDetailCard instanceof HTMLElement) {
        statusDetailCard.textContent = target.dataset.ringSizeStatusDetail || '';
      }
    });

    const nextIcon = target.dataset.ringSizeStatusIcon || '';
    const nextStatusType = target.dataset.ringSizeStatusType || '';
    productContainer?.querySelectorAll('[data-ring-size-status-icon]').forEach((statusIconContainer) => {
      if (statusIconContainer instanceof HTMLElement) {
        statusIconContainer.querySelectorAll('[data-status-icon-option]').forEach((icon) => {
          icon.classList.toggle('hidden', icon.dataset.statusIconOption !== nextIcon);
          icon.classList.toggle('inline-flex', icon.dataset.statusIconOption === nextIcon);
        });

        statusIconContainer.classList.toggle('text-[#A60505]', nextStatusType === 'out_of_stock');
        statusIconContainer.classList.toggle('text-black', nextStatusType !== 'out_of_stock');
      }
    });

    productContainer?.querySelectorAll('[data-ring-size-status-pill]').forEach((statusPill) => {
      if (statusPill instanceof HTMLElement) {
        statusPill.classList.toggle('bg-[#FFF4F4]', nextStatusType === 'out_of_stock');
        statusPill.classList.toggle('text-black', nextStatusType === 'out_of_stock');
        statusPill.classList.toggle('bg-secondary', nextStatusType !== 'out_of_stock');
        statusPill.classList.toggle('text-muted-black', nextStatusType !== 'out_of_stock');
      }
    });

    productContainer?.querySelectorAll('[data-ring-size-submit-text]').forEach((submitText) => {
      if (submitText instanceof HTMLElement) {
        submitText.textContent = target.dataset.ringSizeAddToCartText || '';
      }
    });

    productContainer?.querySelectorAll('[data-ring-size-submit-price]').forEach((submitPrice) => {
      if (submitPrice instanceof HTMLElement) {
        submitPrice.textContent = target.dataset.ringSizePrice || '';
      }
    });

    this.querySelectorAll('[data-ring-size-submit]').forEach((submitButton) => {
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = target.dataset.ringSizeCanAddToCart !== 'true';
      }
    });

    productContainer?.querySelectorAll('[data-ring-size-actions-standard]').forEach((actions) => {
      if (actions instanceof HTMLElement) {
        actions.classList.toggle('hidden', nextStatusType === 'out_of_stock');
      }
    });

    productContainer?.querySelectorAll('[data-ring-size-actions-oos]').forEach((actions) => {
      if (actions instanceof HTMLElement) {
        actions.classList.toggle('hidden', nextStatusType !== 'out_of_stock');
      }
    });
  }

  #syncSecondOptionUi(target) {
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.dataset.secondOptionValue) return;

    const productContainer = this.closest('.shopify-section, dialog, product-card');
    productContainer?.querySelectorAll('[data-second-option-trigger-value]').forEach((triggerValue) => {
      if (triggerValue instanceof HTMLElement) {
        triggerValue.textContent = target.dataset.secondOptionValue || '';
      }
    });

    productContainer?.querySelectorAll('[data-second-option-trigger-swatch]').forEach((triggerSwatch) => {
      if (triggerSwatch instanceof HTMLImageElement) {
        triggerSwatch.src = target.dataset.secondOptionSwatchSrc || '';
        triggerSwatch.alt = target.dataset.secondOptionSwatchAlt || target.dataset.secondOptionValue || '';
      }
    });

    this.#syncSecondOptionSwatchVisibility();

    productContainer?.querySelectorAll('[data-second-option-item]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const input = item.querySelector('input[data-second-option-value]');
      const frame = item.querySelector('[data-second-option-swatch-frame]');
      if (!(input instanceof HTMLInputElement) || !(frame instanceof HTMLElement)) return;

      frame.classList.toggle('border-gray-400', input.checked);
      frame.classList.toggle('border-transparent', !input.checked);
    });

    productContainer?.querySelectorAll('[data-second-option-status-detail]').forEach((statusDetail) => {
      if (statusDetail instanceof HTMLElement) {
        statusDetail.textContent = target.dataset.secondOptionStatusDetail || '';
      }
    });

    const nextIcon = target.dataset.secondOptionStatusIcon || '';
    const nextStatusType = target.dataset.secondOptionStatusType || '';
    productContainer?.querySelectorAll('[data-second-option-status-icon]').forEach((statusIconContainer) => {
      if (statusIconContainer instanceof HTMLElement) {
        statusIconContainer.querySelectorAll('[data-status-icon-option]').forEach((icon) => {
          icon.classList.toggle('hidden', icon.dataset.statusIconOption !== nextIcon);
          icon.classList.toggle('inline-flex', icon.dataset.statusIconOption === nextIcon);
        });

        statusIconContainer.classList.toggle('text-[#A60505]', nextStatusType === 'out_of_stock');
        statusIconContainer.classList.toggle('text-black', nextStatusType !== 'out_of_stock');
      }
    });

    productContainer?.querySelectorAll('[data-second-option-status-pill]').forEach((statusPill) => {
      if (statusPill instanceof HTMLElement) {
        statusPill.classList.toggle('w-fit', nextStatusType === 'out_of_stock');
        statusPill.classList.toggle('rounded-full', nextStatusType === 'out_of_stock');
        statusPill.classList.toggle('bg-[#FFF4F4]', nextStatusType === 'out_of_stock');
        statusPill.classList.toggle('px-4', nextStatusType === 'out_of_stock');
        statusPill.classList.toggle('py-2', nextStatusType === 'out_of_stock');
        statusPill.classList.toggle('text-black', nextStatusType === 'out_of_stock');
        statusPill.classList.toggle('justify-center', nextStatusType !== 'out_of_stock');
      }
    });

    productContainer?.querySelectorAll('[data-second-option-status-detail]').forEach((statusDetail) => {
      if (statusDetail instanceof HTMLElement) {
        statusDetail.classList.toggle('truncate', nextStatusType !== 'out_of_stock');
        statusDetail.classList.toggle('text-center', nextStatusType !== 'out_of_stock');
      }
    });

    productContainer?.querySelectorAll('[data-second-option-submit-text]').forEach((submitText) => {
      if (submitText instanceof HTMLElement) {
        submitText.textContent = target.dataset.secondOptionAddToCartText || '';
      }
    });

    productContainer?.querySelectorAll('[data-second-option-submit-price]').forEach((submitPrice) => {
      if (submitPrice instanceof HTMLElement) {
        submitPrice.textContent = target.dataset.secondOptionPrice || '';
      }
    });

    this.querySelectorAll('[data-second-option-submit]').forEach((submitButton) => {
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = target.dataset.secondOptionCanAddToCart !== 'true';
      }
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.#syncRingSizeUi(event.target);
    this.#syncSecondOptionUi(event.target);

    if (event.target instanceof HTMLInputElement && event.target.dataset.ringSizeValue) {
      const slideoutSubmitButton = this.querySelector('[data-ring-size-submit]');
      if (slideoutSubmitButton instanceof HTMLButtonElement) {
        slideoutSubmitButton.disabled = true;
      }
    }

    if (event.target instanceof HTMLInputElement && event.target.dataset.secondOptionValue) {
      const secondOptionSubmitButton = this.querySelector('[data-second-option-submit]');
      if (secondOptionSubmitButton instanceof HTMLButtonElement) {
        secondOptionSubmitButton.disabled = true;
      }
    }

    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (event.target.dataset.variantId) {
        url.searchParams.set('variant', event.target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // Quick view and swatch-driven pickers need the lightweight product-card section response.
    if (
      this.closest('quick-add-component') ||
      this.closest('quick-add-dialog') ||
      this.closest('swatches-variant-picker-component')
    ) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        //html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantScripts = html.querySelectorAll(`variant-picker script[type="application/json"]`);
        const textContent = variantScripts[variantScripts.length - 1]?.textContent;
        if (!textContent) return;
        const isQuickViewPicker = !!this.closest('quick-add-dialog');

        if (shouldMorphMain) {
          this.updateMain(html);
        } else if (isQuickViewPicker) {
          const nextVariantPicker = html.querySelector(this.tagName.toLowerCase());

          if (nextVariantPicker instanceof HTMLElement) {
            if (nextVariantPicker.dataset.productId) {
              this.dataset.productId = nextVariantPicker.dataset.productId;
            }

            if (nextVariantPicker.dataset.productUrl) {
              this.dataset.productUrl = nextVariantPicker.dataset.productUrl;
            }
          }

          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
              })
            );
          }
        } else {
          const newProduct = this.updateVariantPicker(html);
          const currentProductPrice = this.closest('[data-quick-view-content], .shopify-section, main')?.querySelector('product-price');
          const nextProductPrice = html.querySelector('product-price');

          if (currentProductPrice && nextProductPrice) {
            currentProductPrice.innerHTML = nextProductPrice.innerHTML;
          }

          const productSection = this.closest('.shopify-section');
          productSection?.querySelectorAll('[data-product-specification-details]').forEach((currentDetails) => {
            const nextDetails = html.getElementById(currentDetails.id);
            if (nextDetails) morph(currentDetails, nextDetails);
          });
          
          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    // Store old product IDs from product recommendations before morphing
    const oldProductRecommendations = Array.from(main.querySelectorAll('product-recommendations'));
    const oldProductIds = new Map();
    oldProductRecommendations.forEach((el) => {
      if (el.id && el.dataset.productId) {
        oldProductIds.set(el.id, el.dataset.productId);
      }
    });

    morph(main, newMain);

    // Re-initialize Alpine.js on the updated section
    if (window.Alpine && typeof Alpine.initTree === 'function') {
      Alpine.initTree(main);
    }

    // Reset and reload product recommendations if product ID changed
    // After morphing, check all product-recommendations components
    const newProductRecommendations = Array.from(main.querySelectorAll('product-recommendations'));
    newProductRecommendations.forEach((el) => {
      if (!el.id || !el.dataset.productId) return;

      const oldProductId = oldProductIds.get(el.id);
      const newProductId = el.dataset.productId;

      // If product ID changed, reset and reload recommendations
      if (oldProductId && oldProductId !== newProductId) {
        // Remove the attribute to allow reload
        el.removeAttribute('data-recommendations-performed');
        // Trigger MutationObserver by setting a temporary data attribute
        // This will cause the observer to detect the change and reload
        // Use requestAnimationFrame to ensure the mutation is detected after morph completes
        requestAnimationFrame(() => {
          el.setAttribute('data-trigger-reload', 'true');
          requestAnimationFrame(() => {
            el.removeAttribute('data-trigger-reload');
          });
        });
      } else if (!oldProductId && el.dataset.recommendationsPerformed === 'true') {
        // New component that might have been morphed in place with old data
        // Remove the attribute to ensure it can load
        el.removeAttribute('data-recommendations-performed');
      }
    });
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset input:checked, [data-quick-view-option-button][data-selected="true"]'
      )
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
